import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import FormSectionWrapper from "../../../Components/ProForms/FormSectionWrapper";
import ProFormsLayout from "../../../Components/ProForms/ProFormsLayout";
import { useForm } from "react-hook-form";
import ProTextInput from "../../../Components/Inputs/TextField";
import DateField from "../../../Components/Inputs/DateField";
import ProSelectInput from "../../../Components/Inputs/SelectField";
import { useNavigate, useParams } from "react-router-dom";
import CheckBoxGroup from "../../../Components/Inputs/CheckBoxGroup";
import { ProjectInfoConfig } from "../projectData.js";
import ToggleField from "../../../Components/Inputs/ToggleButton";
import TimeField from "../../../Components/Inputs/TimeField";
import ProRadioGroup from "../../../Components/Inputs/RadioGroup";
import { Grid } from "@mui/material";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import { UPDATE_PROJECT_INFO, GET_PROJECT_INFO, ADD_PROJECT_INFO, DYNAMIC_DROPDOWN, GET_ALL_PROJECT_CONTACT_INFO, GET_CHECK_EXISTING_DOCUMENTS } from "../../../../utils/services/apiPath";
import { postApi, getApi } from "../../../../utils/services";
import {
  changeMode,
  checkEditEnabled,
  convertResponseDate,
  convertResponseDateTime,
  convertTimeToResponse,
  formatPayloadDate,
  prepareDefaultValues,
  prepareInitialConfig,
  toBoolean
} from "../../../../utils/formHelper";
import {
  CPRVersionDropDown,
  ContractorOptions,
  CourseContentDeliveryOptions,
  ElearningHoursPaidOptions,
  RelatedInstructionIsProvidedOptions,
  TrackingMethod,
  fringePayModeOptions,
  payrollTypeOptions,
  timekeepingOptions,
  weekdaysOptions,
  yesNoOptions,
  isAmendedCprOptions,
  isSupplementCprOptions
} from "../../../../utils/dropdownHelper.js";
import usePermission from "../../../../utils/customHooks/usePermission";
import { R_PROJECTS, R_PROJECTS_INFO } from "../../../../utils/permissionReferenceConfig";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import ProjectContactInfo from "./ProjectContactInfo.jsx";
import DialogModal from "../../../Components/DialogModel/index.js";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";

export default function ProjectInfo() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(ProjectInfoConfig));
  const { handleSubmit, control, setValue, watch, clearErrors } = useForm({ defaultValues: prepareDefaultValues(ProjectInfoConfig) });
  const [isSaving, setIsSaving] = useState(false);
  const [projectProfileData, setProjectProfileData] = useState({});
  const [projectInfoId, setProjectInfoId] = useState(null);
  const [timeKeepingPeriodArray] = useState([]);
  const [globalEditMode, setGlobalEditMode] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const [uploadTimesheetData, setUploadTimesheetData] = useState([]);
  const autoGenerate = watch("isCertifiedPayrollAutoGenarate");
  const token = localStorage.getItem("accessToken");
  const tokenData = jwtDecode(token);
  const [hasHRContact, setHasHRContact] = useState(false);
  const [isConfirmRemoveTimePay, setIsConfirmRemoveTimePay] = useState(false);
  const [isTimesheetGenerated, setIsTimesheetGenerated] = useState(false);
  const [isCprGenerated, setIsCprGenerated] = useState(false);
  const [showCprAlert, setShowCprAlert] = useState(false);
  const [savedWorkforceStartDate, setSavedWorkforceStartDate] = useState(null);
  const contactRef = useRef();
  const [editSource, setEditSource] = useState(null);

  


  const { "My Project Information": projectInformationPermission, "Apprentice Mgmt": apprenticeMgmtPermission } = usePermission(
    subModPermissionSelector(R_PROJECTS, R_PROJECTS_INFO),
    ["My Project Information", "Apprentice Mgmt"]
  );

  const workforceStartDate = watch("workforceStartDate");

  const handleDateValidation = date => {
    return date ? dayjs(date).isBefore(dayjs(workforceStartDate), "day") : false;
  };

  const isEditModeOn = useMemo(() => checkEditEnabled(false, formConfig), [formConfig]);

  const backAction = () => navigate("/projects");

  const checkForHRContact = useCallback(async () => {
    try {
      const payload = {
        pageIndex: 1,
        pageSize: 100,
        searchString: "",
        orderByAscending: true,
        orderCol: "",
      };
      const { data, error } = await postApi(GET_ALL_PROJECT_CONTACT_INFO, payload, { params: { projectId: id } });
      if (error) {
        errorToast(error);
        return false;
      }
      // Check if any contact has HR type
      const hasHR = data?.some(contact => contact.contactType?.toLowerCase() === 'hr');
      setHasHRContact(hasHR);
      return hasHR;
    } catch (error) {
      console.error("Error checking HR contact:", error);
      return false;
    }
  }, [id]);

  const onEdit = () => enterEditMode("parent");

  const onCancel = () => {
    setGlobalEditMode(false);
    setFormConfig(changeMode());
    clearErrors();
  };
  const handleReadValueClick = () => {
    onEdit();
  };

  useEffect(() => {
    if (isEditModeOn && !autoGenerate) {
      setConfirmation(true);
    }
  }, [autoGenerate]);

  const onSubmit = async values => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      if (!globalEditMode) {
        setGlobalEditMode(true);
        setEditSource("parent");
        setFormConfig(changeMode("edit"));
      }

      const isContactSaved = await contactRef.current?.saveContactFromParent();

      if (!isContactSaved) {
        errorToast("Please complete required Contact fields before saving.");
        return;
      }
      
      const hasHR = await checkForHRContact();
      if (!hasHR) {
        errorToast("An HR contact is required before saving the project. Please add an HR contact first.");
        return;
      }

      
      const {
        adaptiveElearning,
        courseContentDelivery,
        learningHoursPaid,
        maxPaidHoursPerDay,
        fringe,
        wageRate,
        learningOrClassHoursPaid,
        relatedInstructionProvidedHours,
        rtiWeeklyExpectationHours,
        preferredRTIDays,
        certifiedPayrollEmailAddress,
        totalWorkforceHours,
        typicalshiftdays,
        shiftStartTime,
        shiftEndTime,
        preferredDayStartTime,
        preferredDayEndTime,
        workforceStartDate,
        workForceEndDate,
        uploadDailyTimeSheet,
        ...rest
      } = values;

      const payload = {
        ...rest,
        projectId: id,
        projectInfoId: projectInfoId,
        uploadDailyTimeSheet: 2,
        ownerCertifiedPayrollSignatoryEmail: null,
        percentageApprenticeWork: totalWorkforceHours,
        typicalShiftDay: typicalshiftdays?.length > 0 ? Array.from(new Set(typicalshiftdays)).toString() : "",
        shiftStartTime: convertTimeToResponse(shiftStartTime),
        shiftEndTime: convertTimeToResponse(shiftEndTime),
        mobileAppTimeKeepingManagement: timeKeepingPeriodArray?.length > 0 ? Array.from(new Set(timeKeepingPeriodArray)).toString() : "",
        payPeriodBegin: values.daysofWeekBegin,
        payPeriodEnd: values.daysofWeekEnd,
        workforceStartDate: formatPayloadDate(workforceStartDate),
        workForceEndDate: formatPayloadDate(workForceEndDate),
        apprenticeManagementModel: {
          fringe,
          wageRate,
          courseContentDelivery,
          maxPaidHoursPerDay,
          learningHoursPaid,
          learningOrClassHoursPaid,
          relatedInstructionProvidedHours,
          rtiWeeklyExpectationHours,
          rtiWeeklyExpectationMinutes: values.rtiWeeklyExpectationMins,
          isAdaptive: adaptiveElearning,
          startTime: convertTimeToResponse(preferredDayStartTime),
          endTime: convertTimeToResponse(values.preferredDaysEndTime),
          preferredDays: preferredRTIDays?.length > 0 ? Array.from(new Set(preferredRTIDays)).toString() : ""
        },
        payrollOn: "1",
        provideTimeKeepingOn: "1",
        isCertifiedPayrollAutoGenarate: true,
        isAmendedCpr: toBoolean(values.isAmendedCpr),
        isSupplementCpr: toBoolean(values.isSupplementCpr),
        moreThanFourEmployees: toBoolean(values.moreThanFourEmployees),
      };

      const { error } = await postApi(projectInfoId ? UPDATE_PROJECT_INFO : ADD_PROJECT_INFO, payload);
      if (error) {
        errorToast(error);
        return;
      }

      if (projectInfoId) {
        setProjectProfileData(values);
        getProjectProfileData(id);
        onCancel();
      } else {
        navigate("/projects");
      }
      successToast("Project edited successfully.");
    } catch (error) {
      console.error("Error saving project:", error);
      errorToast("Error saving project. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getProjectProfileData = useCallback(
    async id => {
      try {
        const { error, data: project } = await postApi(GET_PROJECT_INFO, { projectId: id });
        if (error) {
          errorToast(error);
          return;
        }
        if (!project) return;

        const learningHoursPaidValue = getLearningHoursPaidValue(project?.apprenticeManagementModel.learningHoursPaid);
        const data = transformProjectData(project, learningHoursPaidValue);

        setProjectProfileData(data);
        setProjectInfoId(project.projectInfoId);
        updateFormFields(data, formConfig, setValue);
        setSavedWorkforceStartDate(data?.workforceStartDate);

        if (data?.workforceStartDate) {
          try {
            const result = await getApi(`${GET_CHECK_EXISTING_DOCUMENTS}${id}/checkExistingDocuments/${formatPayloadDate(data.workforceStartDate)}`);
            if (result?.hasTimesheets) {
              setIsTimesheetGenerated(true);
            }
            if (result?.isExistingCpr) {
              setIsCprGenerated(true);
            }
          } catch (error) {
            console.error("Error checking existing CPR documents:", error);
            errorToast("Error checking existing CPR documents. Please try again later.");
          }
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
        errorToast("Error fetching project data. Please try again later.");
      }
    },
    [formConfig, setValue]
  );

  const checkExistingDocuments = useCallback(async () => {
    try {
      const result = await getApi(`${GET_CHECK_EXISTING_DOCUMENTS}${id}/checkExistingDocuments/${formatPayloadDate(workforceStartDate)}`);
      if (result?.isExistingCpr) {
        setIsCprGenerated(true);
        setShowCprAlert(true);
        return false;
      } else if (result?.isExistingPayroll || result?.isExistingTimesheet) {
        if (result?.hasTimesheets) {
          setIsTimesheetGenerated(true);
        }
        setIsConfirmRemoveTimePay(true);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error checking timeheet/payroll-", error);
      return false;
    }
  }, [id, workforceStartDate]);

  const getLearningHoursPaidValue = learningHoursPaid => {
    if (learningHoursPaid === true) return true;
    if (learningHoursPaid === false) return false;
    return null;
  };

  const transformProjectData = (project, learningHoursPaidValue) => {
    const { apprenticeManagementModel = {} } = project || {};
    return {
      ...project,
      shiftEndTime: convertResponseDateTime(project?.shiftEndTime),
      shiftStartTime: convertResponseDateTime(project?.shiftStartTime),
      totalWorkforceHours: project?.percentageApprenticeWork,
      workforceStartDate: convertResponseDate(project?.workforceStartDate),
      workForceEndDate: convertResponseDate(project?.workforceEndDate),
      certifiedPayrollEmailAddress: project?.ownerCertifiedPayrollSignatoryEmail,
      typicalshiftdays: splitAndFilter(project?.typicalShiftDay),
      rtiWeeklyExpectationHours: apprenticeManagementModel.rtiWeeklyExpectationHours || null,
      rtiWeeklyExpectationMins: apprenticeManagementModel.rtiWeeklyExpectationMinutes || null,
      adaptiveElearning: apprenticeManagementModel.isAdaptive || false,
      courseContentDelivery: apprenticeManagementModel.courseContentDelivery || null,
      apprenticeHoursTracking: "Manual Timesheet",
      preferredDays: apprenticeManagementModel.preferredDays || null,
      maxPaidHoursPerDay: apprenticeManagementModel.maxPaidHoursPerDay || null,
      wageRate: apprenticeManagementModel.wageRate || null,
      fringe: apprenticeManagementModel.fringe || null,
      preferredDayStartTime: convertResponseDateTime(apprenticeManagementModel.startTime),
      preferredDaysEndTime: convertResponseDateTime(apprenticeManagementModel.endTime),
      learningHoursPaid: learningHoursPaidValue,
      learningOrClassHoursPaid: apprenticeManagementModel.learningOrClassHoursPaid || null,
      preferredRTIDays: splitAndFilter(apprenticeManagementModel.preferredDays),
      relatedInstructionProvidedHours: apprenticeManagementModel.relatedInstructionProvidedHours || null,
      daysofWeekBegin: project?.payPeriodBegin,
      daysofWeekEnd: project?.payPeriodEnd,
      isAmendedCpr: project?.isAmendedCpr ?? false,
      isSupplementCpr: project?.isSupplementCpr ?? false,
    };
  };

  const splitAndFilter = str => str?.split(",").filter(v => v) || [];

  const updateFormFields = (data, formConfig, setValue) => {
    Object.keys(formConfig).forEach(field => setValue(field, data[field]));
  };

  const handleConfirmModal = () => {
    setConfirmation(false);
  };

  const handleCancelModal = () => {
    setConfirmation(false);
    setValue("isCertifiedPayrollAutoGenarate", true);
  };

  const handleConfirmRemoveTimePayModal = () => {
    setIsConfirmRemoveTimePay(false);
  };

  const handleCancelRemoveTimePayModal = () => {
    setIsConfirmRemoveTimePay(false);
    setValue("workforceStartDate", convertResponseDate(savedWorkforceStartDate));
  };

  const handleAlertCprGeneratedModal = () => {
    setShowCprAlert(false);
    setValue("workforceStartDate", convertResponseDate(savedWorkforceStartDate));
  };

  const getUploadTimeSheetDropDown = async () => {
    const payload = ["uploaddailytimesheettype"];
    const data = await postApi(DYNAMIC_DROPDOWN, payload);
    setUploadTimesheetData(data[0]?.typeDropDowns?.map(item => ({ label: item.label, value: item.intValue })));
  };

  useEffect(() => {
    getProjectProfileData(id);
    getUploadTimeSheetDropDown();
  }, [id]);

  useEffect(() => {
    if(workforceStartDate && formatPayloadDate(savedWorkforceStartDate) !== formatPayloadDate(workforceStartDate)) {
      checkExistingDocuments();
    }
  }, [workforceStartDate]);

  const defaultFormProps = { control, formValues: projectProfileData, handleReadValueClick };
  const courseContentDelivery = watch("courseContentDelivery");

  const fieldPermissions = {
    canView: projectInformationPermission.canView,
    canAdd: projectInformationPermission.canAdd,
    canEdit: projectInformationPermission.canEdit
  };

  const exitEditMode = () => {
    setGlobalEditMode(false);
    setEditSource(null);
    setFormConfig(changeMode());
  };

  const enterEditMode = (source = "parent") => {
    setGlobalEditMode(true);
    setEditSource(source);
    setFormConfig(changeMode("edit"));
  };

  return (
    <div className="partner-profile">
      <ProFormsLayout
        backAction={backAction}
        title={`Edit Project`}
        backLabel="Go Back to All Projects"
        beingEdited={globalEditMode}
        onEdit={onEdit}
        onCancel={onCancel}
        onSave={handleSubmit(onSubmit)}
        hideEdit={!(projectInformationPermission.canEdit || apprenticeMgmtPermission.canEdit)}
        isSaving={isSaving}
      >
        <div className="proFormSample-content">
          {projectInformationPermission.canView ? (
            <FormSectionWrapper title={"My Project Information"}>
              <ProTextInput
                {...defaultFormProps}
                {...formConfig.totalWorkforceHours}
                permission={projectInformationPermission.fieldPerObj[formConfig.totalWorkforceHours.perKey]}
              />
              <ProSelectInput
                {...defaultFormProps}
                {...formConfig.moreThanFourEmployees}
                options={yesNoOptions}
                permission={projectInformationPermission.fieldPerObj[formConfig.moreThanFourEmployees.perKey]}
              />
              <Grid md={4} xs={12}></Grid>
              <CheckBoxGroup
                {...defaultFormProps}
                {...formConfig.typicalshiftdays}
                options={weekdaysOptions}
                permission={projectInformationPermission.fieldPerObj[formConfig.typicalshiftdays.perKey]}
              />
              <TimeField
                {...defaultFormProps}
                {...formConfig.shiftStartTime}
                permission={projectInformationPermission.fieldPerObj[formConfig.shiftStartTime.perKey]}
              />
              <TimeField
                {...defaultFormProps}
                {...formConfig.shiftEndTime}
                permission={projectInformationPermission.fieldPerObj[formConfig.shiftEndTime.perKey]}
              />
              <ProTextInput
                {...defaultFormProps}
                {...formConfig.employeeCount}
                permission={projectInformationPermission.fieldPerObj[formConfig.employeeCount.perKey]}
              />
              <ProRadioGroup
                {...defaultFormProps}
                {...formConfig.daysofWeekBegin}
                options={weekdaysOptions}
                permission={{
                  ...projectInformationPermission.fieldPerObj[formConfig.daysofWeekBegin.perKey],
                  canEdit: isTimesheetGenerated ? false : projectInformationPermission.fieldPerObj[formConfig.daysofWeekBegin.perKey]?.canEdit
                }}
              />
              <ProRadioGroup
                {...defaultFormProps}
                {...formConfig.daysofWeekEnd}
                options={weekdaysOptions}
                permission={{
                  ...projectInformationPermission.fieldPerObj[formConfig.daysofWeekEnd.perKey],
                  canEdit: isTimesheetGenerated ? false : projectInformationPermission.fieldPerObj[formConfig.daysofWeekEnd.perKey]?.canEdit
                }}
              />
              <DateField
                {...defaultFormProps}
                {...formConfig.workforceStartDate}
                permission={projectInformationPermission.fieldPerObj[formConfig.workforceStartDate.perKey]}
              />
              <DateField
                {...defaultFormProps}
                {...formConfig.workForceEndDate}
                permission={projectInformationPermission.fieldPerObj[formConfig.workForceEndDate.perKey]}
                shouldDisableDate={handleDateValidation}
              />
              <ToggleField {...defaultFormProps} {...formConfig.adminAssignDailyApprenticeMentor} permission={fieldPermissions} />
              <ToggleField {...defaultFormProps} {...formConfig.adminAssignDailyWPS} permission={fieldPermissions} />
              <ProRadioGroup {...defaultFormProps} {...formConfig.fringePayMode} options={fringePayModeOptions} permission={fieldPermissions} />
              <ProRadioGroup {...defaultFormProps} {...formConfig.contractorType} options={ContractorOptions} permission={fieldPermissions} />
              
              {useFeatureFlagEnabled('is_v2_cpr_format_enabled') &&
                <ProSelectInput {...defaultFormProps} {...formConfig.cprVersionLabel} options={CPRVersionDropDown} permission={fieldPermissions} />
              }
              </FormSectionWrapper>
          ) : null}

          <FormSectionWrapper title={"Project Contacts"}>
            <ProjectContactInfo ref={contactRef} globalEditMode={globalEditMode} editSource={editSource} onContactEdit={() => enterEditMode("contact")} onContactCancel={exitEditMode}
            />
          </FormSectionWrapper>

          {apprenticeMgmtPermission.canView ? (
            <FormSectionWrapper title={"Apprentice Management"}>
              <ToggleField
                {...defaultFormProps}
                {...formConfig.adaptiveElearning}
                permission={apprenticeMgmtPermission.fieldPerObj[formConfig.adaptiveElearning.perKey]}
              />
              <ProSelectInput
                {...defaultFormProps}
                {...formConfig.courseContentDelivery}
                options={CourseContentDeliveryOptions}
                permission={apprenticeMgmtPermission.fieldPerObj[formConfig.courseContentDelivery.perKey]}
              />
              {String(courseContentDelivery) === "1" && (
                <>
                  <ProSelectInput
                    {...defaultFormProps}
                    {...formConfig.learningHoursPaid}
                    options={ElearningHoursPaidOptions}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.learningHoursPaid.perKey]}
                  />
                  <ProSelectInput
                    {...defaultFormProps}
                    {...formConfig.relatedInstructionProvidedHours}
                    options={RelatedInstructionIsProvidedOptions}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.relatedInstructionProvidedHours.perKey]}
                  />
                  <CheckBoxGroup
                    {...defaultFormProps}
                    {...formConfig.preferredRTIDays}
                    options={weekdaysOptions}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.preferredRTIDays.perKey]}
                  />
                  <TimeField
                    {...defaultFormProps}
                    {...formConfig.preferredDayStartTime}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.preferredDayStartTime.perKey]}
                  />
                  <TimeField
                    {...defaultFormProps}
                    {...formConfig.preferredDaysEndTime}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.preferredDaysEndTime.perKey]}
                  />
                </>
              )}

              {String(courseContentDelivery) === "2" && (
                <>
                  <ProTextInput
                    {...defaultFormProps}
                    {...formConfig.maxPaidHoursPerDay}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.maxPaidHoursPerDay.perKey]}
                  />
                  <ProTextInput
                    {...defaultFormProps}
                    {...formConfig.rtiWeeklyExpectationHours}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.rtiWeeklyExpectationHours.perKey]}
                  />
                  <ProTextInput
                    {...defaultFormProps}
                    {...formConfig.rtiWeeklyExpectationMins}
                    permission={apprenticeMgmtPermission.fieldPerObj[formConfig.rtiWeeklyExpectationMins.perKey]}
                  />
                </>
              )}
            </FormSectionWrapper>
          ) : null}
        </div>
      </ProFormsLayout>
      <DialogModal
        show={confirmation}
        title="Confirm"
        message={`Please acknowledge that by uploading a Certified Payroll report, you are acknowledging that ProScore did not generate the Certified Payroll Report for this pay period and that ProScore is not responsible for the accuracy and completeness of the contents of the uploaded Certified Payroll Report. As such, any audits, inquiries, or exception filing for this Certified Payroll Report will be directed back to ${tokenData?.GlobalClientName}`}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}
      />
      <DialogModal
        show={isConfirmRemoveTimePay}
        title="Confirm"
        message={`Changing the start date will remove timesheet/payroll data. Do you still want to continue?`}
        onConfirm={handleConfirmRemoveTimePayModal}
        onCancel={handleCancelRemoveTimePayModal}
        confirmBtnText={"Yes"}
        cancelBtnText={"No"}
      />
      <DialogModal
        show={showCprAlert}
        title="Alert"
        message={`Start date and pay period dates cannot be changed once a CPR or statement of non-performance has been generated. To make adjustments, please contact ProScore Support.`}
        onConfirm={handleAlertCprGeneratedModal}
        showCancel={false}
        confirmBtnText={"Okay"}
      />
    </div>
  );
}
